import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Enquiry } from '../case-enquiry.component';
import { ModalComponent } from '../../../shared/components/modal/modal.component';
import { Router } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { CaseEnquiryService } from '../../../core/services/case-enquiry.service';

@Component({
    selector: 'app-case-enquiry-details',
    standalone: true,
    imports: [CommonModule, ModalComponent],
    templateUrl: './case-enquiry-details.component.html',
    styleUrl: './case-enquiry-details.component.scss'
})
export class CaseEnquiryDetailsComponent {
    @Input() enquiry: Enquiry | null = null;
    @Output() close = new EventEmitter<void>();
    @Output() statusChanged = new EventEmitter<void>();

    isSubmitting = false;

    private settlementStatusLabels: Record<string, string> = {
        PRE_SUIT: 'In Pre-Suit Negotiations',
        LITIGATION: 'In Litigation',
        SETTLED: 'Settled'
    };

    getSettlementStatusLabel(value: string | undefined): string {
        return value ? (this.settlementStatusLabels[value] || value) : '';
    }

    constructor(
        private authService: AuthService,
        private router: Router,
        private caseEnquiryService: CaseEnquiryService
    ) { }

    get isAdmin(): boolean {
        return this.authService.getRole() === 'ADMIN';
    }

    /** Show Accept/Reject only for admin when status is still New */
    get showAdminActions(): boolean {
        if (!this.isAdmin || !this.enquiry?.inquiries_status) return false;
        return this.enquiry.inquiries_status.toLowerCase() === 'new';
    }

    /** True when enquiry has been accepted */
    get isAccepted(): boolean {
        if (!this.enquiry?.inquiries_status) return false;
        return ['accepted', 'accept'].includes(this.enquiry.inquiries_status.toLowerCase());
    }

    /** Show "Submit Case Details" only for attorneys when accepted and not yet submitted */
    get showSubmitCaseButton(): boolean {
        return !this.isAdmin && this.isAccepted && this.enquiry?.has_submission !== 1;
    }

    onClose() {
        this.close.emit();
    }

    getStatusClass(status: string | undefined): string {
        if (!status) return '';
        const s = status.toLowerCase();
        if (s === 'new') return 'new';
        if (['accept', 'accepted'].includes(s)) return 'accepted';
        if (['reject', 'rejected'].includes(s)) return 'rejected';
        return '';
    }

    acceptEnquiry() {
        if (!this.enquiry?.case_enquiry_id || this.isSubmitting) return;
        this.isSubmitting = true;
        this.caseEnquiryService.updateEnquiryStatus(this.enquiry.case_enquiry_id, 'accept', '').subscribe({
            next: () => {
                if (this.enquiry) this.enquiry.inquiries_status = 'Accepted';
                this.isSubmitting = false;
                this.statusChanged.emit();
            },
            error: () => { this.isSubmitting = false; }
        });
    }

    rejectEnquiry() {
        if (!this.enquiry?.case_enquiry_id || this.isSubmitting) return;
        this.isSubmitting = true;
        this.caseEnquiryService.updateEnquiryStatus(this.enquiry.case_enquiry_id, 'reject', '').subscribe({
            next: () => {
                if (this.enquiry) this.enquiry.inquiries_status = 'Rejected';
                this.isSubmitting = false;
                this.statusChanged.emit();
            },
            error: () => { this.isSubmitting = false; }
        });
    }

    submitCaseDetail() {
        this.onClose();
        this.router.navigate(['/cases/add-details'], {
            queryParams: { enquiryId: this.enquiry?.case_enquiry_id }
        });
    }
}
